import {
  PLAYER_COLORS,
  type ClientRoomState,
} from "@poppycock/shared";
import { motion } from "framer-motion";
import { useMemo, useState } from "react";
import {
  buildGameOverShareText,
  copyGameOverShare,
} from "../lib/invite";
import { Button, Card, PhaseBadge } from "./ui";

export function GameOverScreen({
  state,
  isHost,
  onLobby,
}: {
  state: ClientRoomState;
  isHost: boolean;
  onLobby: () => void;
}) {
  const [shareStatus, setShareStatus] = useState<"idle" | "copied" | "failed">(
    "idle",
  );

  const standings = useMemo(
    () =>
      [...state.players].sort((a, b) => b.score - a.score || a.name.localeCompare(b.name)),
    [state.players],
  );

  const topScore = standings[0]?.score ?? 0;
  const winners = standings.filter((p) => p.score === topScore && topScore > 0);
  const noScores = topScore === 0;
  const rounds = state.round?.number ?? 0;
  const you = standings.find((p) => p.id === state.you.id);
  const yourPlace =
    you != null
      ? standings.findIndex((p) => p.score === you.score) + 1
      : null;

  const sharePayload = {
    code: state.code,
    rounds,
    contentMode: state.contentMode ?? "text",
    standings: standings.map((p) => ({
      name: p.name,
      score: p.score,
      isYou: p.id === state.you.id,
    })),
    youName: state.you.name,
  };

  const preview = buildGameOverShareText(sharePayload);

  const onShare = async () => {
    const ok = await copyGameOverShare(sharePayload);
    setShareStatus(ok ? "copied" : "failed");
    window.setTimeout(() => setShareStatus("idle"), 2200);
  };

  return (
    <div className="flex flex-1 flex-col">
      <header className="mb-5 flex items-center justify-between gap-3">
        <div>
          <p className="text-xs tracking-wide text-[var(--color-muted)] uppercase">
            {rounds > 0
              ? `${rounds} round${rounds === 1 ? "" : "s"} of pure nonsense`
              : "That's a wrap"}
          </p>
          <h2 className="text-xl font-semibold tracking-tight sm:text-2xl">
            Game over
          </h2>
        </div>
        <PhaseBadge phase="game_over" />
      </header>

      <motion.div
        initial={{ opacity: 0, scale: 0.94, y: 12 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ type: "spring", stiffness: 280, damping: 22 }}
      >
        <Card strong className="mb-4 overflow-hidden text-center">
          <p className="mb-2 text-4xl" aria-hidden>
            {noScores ? "🎭" : winners.length > 1 ? "🤝" : "👑"}
          </p>
          <p className="mb-1 text-xs tracking-[0.2em] text-[var(--color-accent)] uppercase">
            {noScores
              ? "Participation trophies for all"
              : winners.length > 1
                ? "Tied for the crown"
                : "Supreme champion of poppycock"}
          </p>
          {noScores ? (
            <p className="text-lg font-semibold text-[var(--color-cream)]">
              Nobody scored. Iconic, actually.
            </p>
          ) : (
            <ul className="mt-2 flex flex-col items-center gap-2">
              {winners.map((w) => {
                const color =
                  PLAYER_COLORS[w.colorIndex % PLAYER_COLORS.length];
                return (
                  <li
                    key={w.id}
                    className="flex items-center gap-2 text-2xl font-bold tracking-tight sm:text-3xl"
                  >
                    <span
                      className="inline-flex h-9 w-9 items-center justify-center rounded-full text-sm font-bold text-[#141414]"
                      style={{ background: color }}
                    >
                      {w.name.slice(0, 1).toUpperCase()}
                    </span>
                    {w.name}
                    {w.id === state.you.id && (
                      <span className="text-sm font-medium text-[var(--color-muted)]">
                        (you)
                      </span>
                    )}
                  </li>
                );
              })}
              <li className="mt-1 text-sm text-[var(--color-muted)]">
                {topScore.toLocaleString()} points
              </li>
            </ul>
          )}
          {yourPlace != null && !noScores && (
            <p className="mt-4 text-sm text-[var(--color-muted)]">
              {yourPlace === 1
                ? "You sat on the throne. Gloat responsibly."
                : `You finished #${yourPlace} with ${you?.score ?? 0} pts.`}
            </p>
          )}
        </Card>
      </motion.div>

      <Card className="mb-4">
        <p className="mb-3 text-xs font-semibold tracking-wide text-[var(--color-muted)] uppercase">
          Final standings
        </p>
        <ol className="flex flex-col gap-2">
          {standings.map((p, i) => {
            const color = PLAYER_COLORS[p.colorIndex % PLAYER_COLORS.length];
            const medal =
              i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : `${i + 1}.`;
            const isWinner = p.score === topScore && topScore > 0;
            return (
              <li
                key={p.id}
                className={`flex items-center gap-3 rounded-xl border px-3 py-2.5 ${
                  isWinner
                    ? "border-[rgba(57,181,74,0.35)] bg-[rgba(57,181,74,0.1)]"
                    : "border-white/6 bg-black/20"
                } ${!p.connected ? "opacity-45" : ""}`}
              >
                <span className="w-8 shrink-0 text-center text-lg">{medal}</span>
                <span
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-bold text-[#141414]"
                  style={{ background: color }}
                >
                  {p.name.slice(0, 1).toUpperCase()}
                </span>
                <div className="min-w-0 flex-1 truncate font-medium">
                  {p.name}
                  {p.id === state.you.id && (
                    <span className="ml-2 text-[10px] tracking-wide text-[var(--color-muted)] uppercase">
                      you
                    </span>
                  )}
                </div>
                <span className="font-semibold tabular-nums">{p.score}</span>
              </li>
            );
          })}
        </ol>
      </Card>

      <Card className="mb-6">
        <p className="mb-2 text-xs font-semibold tracking-wide text-[var(--color-muted)] uppercase">
          Gloat badge
        </p>
        <p className="mb-3 text-sm text-[var(--color-muted)]">
          Copy a Wordle-style results card for group chats. Paste and preen.
        </p>
        <pre className="mb-4 max-h-48 overflow-auto rounded-xl border border-white/10 bg-black/40 p-4 font-mono text-[12px] leading-relaxed text-[var(--color-cream)] whitespace-pre-wrap">
          {preview}
        </pre>
        <Button className="w-full" onClick={onShare}>
          {shareStatus === "copied"
            ? "Copied — go brag"
            : shareStatus === "failed"
              ? "Copy failed — try again"
              : "Copy results badge"}
        </Button>
      </Card>

      {isHost ? (
        <div className="flex flex-col gap-3 sm:flex-row">
          <Button className="flex-1" onClick={onLobby}>
            Back to lobby
          </Button>
        </div>
      ) : (
        <p className="text-center text-sm text-[var(--color-muted)]">
          Waiting for host — or copy that badge and leave a legend.
        </p>
      )}
    </div>
  );
}
